import React from 'react'
import { useStateParams } from '../../utilities/hooks'
import { DateTime, DateTimeMaybeValid } from 'luxon'
import { Grouping } from '../../constants/grouping'
import { AppContext } from '../../App'
import { PageStatus } from '../../constants/pageStatus'
import { ClientGroup } from '../../../../back-end/common/clientGroup'
import { Staff } from '../../../../back-end/common/staff'
import { ChevronLeft } from 'react-feather'
import IntroScreen from '../../components/wip/introScreen'
import { WipData } from '../../../../back-end/common/wip'

import * as Request from '../../utilities/request'
import ListingScreen from '../../components/wip/listingScreen'
import { Client } from '../../../../back-end/common/client'
import { Job } from '../../../../back-end/common/job'
import { SelectOption } from '../../components/ui/Form/Select'

export interface Filters {
	endDate: DateTime
	setEndDate: (value: DateTime) => void
	grouping: Grouping
	setGrouping: (value: Grouping) => void
	familyGroupId: string
	setFamilyGroupId: (value: string) => void
	clientId: string
	setClientId: (value: string) => void
	clientPartnerId: string
	setClientPartnerId: (value: string) => void
	clientManagerId: string
	setClientManagerId: (value: string) => void
	jobPartnerId: string
	setJobPartnerId: (value: string) => void
	jobManagerId: string
	setJobManagerId: (value: string) => void
	jobStaffId: string
	setJobStaffId: (value: string) => void
	jobNumber: string
	setJobNumber: (value: string) => void
	jobType: string
	setJobType: (value: string) => void
}

const WIP = () => {
	const context = React.useContext(AppContext)
	const [pageStatus, setPageStatus] = React.useState<PageStatus>('Loading')

	const [endDate, setEndDate] = useStateParams<DateTimeMaybeValid>(
		DateTime.now(),
		'endDate',
		(d) => d.toISODate() || '',
		(d) => DateTime.fromISO(d)
	)
	const [grouping, setGrouping] = useStateParams<Grouping>(
		Grouping.fcje,
		'grouping',
		(d) => d.value,
		(d) => Grouping[d as keyof typeof Grouping]
	)

	const [familyGroupId, setFamilyGroupId] = useStateParams(
		'',
		'familyGroupId',
		(d) => d,
		(d) => d
	)

	const [clientId, setClientId] = useStateParams(
		'',
		'clientId',
		(d) => d,
		(d) => d
	)

	const [clientPartnerId, setClientPartnerId] = useStateParams(
		'',
		'clientPartnerId',
		(d) => d,
		(d) => d
	)

	const [clientManagerId, setClientManagerId] = useStateParams(
		'',
		'clientManagerId',
		(d) => d,
		(d) => d
	)

	const [jobPartnerId, setJobPartnerId] = useStateParams(
		'',
		'jobPartnerId',
		(d) => d,
		(d) => d
	)

	const [jobManagerId, setJobManagerId] = useStateParams(
		'',
		'jobManagerId',
		(d) => d,
		(d) => d
	)

	const [jobStaffId, setJobStaffId] = useStateParams(
		'',
		'jobStaffId',
		(d) => d,
		(d) => d
	)

	const [jobNumber, setJobNumber] = useStateParams(
		'',
		'jobNumber',
		(d) => d,
		(d) => d
	)

	const [jobType, setJobType] = useStateParams(
		'',
		'jobType',
		(d) => d,
		(d) => d
	)

	const filters: Filters = {
		endDate,
		setEndDate,
		grouping,
		setGrouping,
		familyGroupId,
		setFamilyGroupId,
		clientId,
		setClientId,
		clientPartnerId,
		setClientPartnerId,
		clientManagerId,
		setClientManagerId,
		jobPartnerId,
		setJobPartnerId,
		jobManagerId,
		setJobManagerId,
		jobStaffId,
		setJobStaffId,
		jobNumber,
		setJobNumber,
		jobType,
		setJobType,
	}

	const [familyGroups, setFamilyGroups] = React.useState<ClientGroup[]>([])
	const [allStaff, setAllStaff] = React.useState<Staff[]>([])
	const [jobCategories, setJobCategories] = React.useState<string[]>([])

	// Job and Client are searched server-side (async selects), so we only hold the currently selected
	// option (value + label) rather than the whole table. jobNumber / clientId in `filters` remain the
	// source of truth for building the WIP query.
	const [selectedJobOption, setSelectedJobOption] = React.useState<SelectOption<string> | null>(null)
	const [selectedClientOption, setSelectedClientOption] = React.useState<SelectOption<string> | null>(null)

	const [wipData, setWipData] = React.useState<WipData | null>(null)

	const [showListing, setShowListing] = React.useState(false)

	React.useEffect(() => {
		const getData = async () => {
			const [familyGroupsRes, staffRes, jobTypesRes] = await Promise.all([
				Request.get('clientGroup'),
				Request.get('staff'),
				Request.get('job/types'),
			])

			setFamilyGroups(familyGroupsRes.data.clientGroups)
			setAllStaff(staffRes.data.staff)
			setJobCategories(jobTypesRes.data.jobTypes)

			// Resolve labels for a Job / Client restored from the URL so the async selects show them.
			if (jobNumber) {
				const jobRes = await Request.get(`job?where=${encodeURIComponent(`JobNumber==${jobNumber}`)}&page=1&pageSize=1`)
				const job = (jobRes.data.jobs as Job[])[0]
				if (job) {
					setSelectedJobOption({ value: job.number, label: `${job.number} - ${job.name}` })
				}
			}
			if (clientId) {
				const clientRes = await Request.get(`client?where=${encodeURIComponent(`ClientXpmID==${clientId}`)}&page=1&pageSize=1`)
				const client = (clientRes.data.clients as Client[])[0]
				if (client) {
					setSelectedClientOption({ value: client.xpmID, label: client.name })
				}
			}

			setPageStatus('Ready')
		}

		setPageStatus('Loading')
		if (context.appState.authState.isLoggedIn) {
			getData()
		}
	}, [context.appState.authState])

	const loadJobOptions = async (input: string): Promise<SelectOption<string>[]> => {
		const res = await Request.get(`job?search=${encodeURIComponent(input)}&page=1&pageSize=50`)
		return Array.from(
			new Map((res.data.jobs as Job[]).map((j) => [j.number, { value: j.number, label: `${j.number} - ${j.name}` }])).values()
		)
	}

	const loadClientOptions = async (input: string): Promise<SelectOption<string>[]> => {
		const res = await Request.get(`client?search=${encodeURIComponent(input)}&page=1&pageSize=50`)
		return Array.from(new Map((res.data.clients as Client[]).map((c) => [c.xpmID, { value: c.xpmID, label: c.name }])).values())
	}

	const handleJobChange = (option: SelectOption<string> | null) => {
		setSelectedJobOption(option)
		setJobNumber(option ? option.value : '')
	}

	const handleClientChange = (option: SelectOption<string> | null) => {
		setSelectedClientOption(option)
		setClientId(option ? option.value : '')
	}

	const buildWhereString = () => {
		const whereStringComponents: string[] = []

		if (familyGroupId) {
			whereStringComponents.push(`ClientGroupXpmID==${familyGroupId}`)
		}
		if (clientId) {
			whereStringComponents.push(`ClientXpmID==${clientId}`)
		}
		if (clientPartnerId) {
			whereStringComponents.push(`ClientAccountManagerStaffXpmID==${clientPartnerId}`)
		}
		if (clientManagerId) {
			whereStringComponents.push(`ClientJobManagerStaffXpmID==${clientManagerId}`)
		}
		if (jobPartnerId) {
			whereStringComponents.push(`JobPartnerStaffXpmID==${jobPartnerId}`)
		}
		if (jobManagerId) {
			whereStringComponents.push(`JobManagerStaffXpmID==${jobManagerId}`)
		}
		if (jobStaffId) {
			whereStringComponents.push(`TimeStaffXpmID==${jobStaffId}`)
		}
		if (endDate) {
			whereStringComponents.push(`TimeDate<=${endDate.toISODate()}`)
		}
		if (jobNumber) {
			whereStringComponents.push(`JobNumber==${jobNumber}`)
		}
		if (jobType) {
			whereStringComponents.push(`JobType==${jobType}`)
		}

		return whereStringComponents.length > 0 ? '?where=' + encodeURIComponent(whereStringComponents.join('+')) : ''
	}

	const fetchWipRecords = async () => {
		setPageStatus('Loading')
		setWipData(null)

		const whereString = buildWhereString()

		await Request.getPagedData(`wip/${grouping.value}${whereString ? `${whereString}&` : ''}`, 4000, (data) =>
			setWipData(
				(wipData) =>
					({
						grouping: (data as { wipData: WipData }).wipData.grouping,
						familyGroups: [...(wipData?.familyGroups || []), ...(data as { wipData: WipData }).wipData.familyGroups],
					} as unknown as WipData) // this is to get around unioned type of WIPData not playing nicely with the spread operator/casting
			)
		)

		setPageStatus('Ready')
	}

	const handleRunAnalysis = async () => {
		setShowListing(true)
		await fetchWipRecords()
	}

	const backButton = (
		<div onClick={() => setShowListing(false)} className="center-flex span-navy span-semi span-link">
			<ChevronLeft size={16} />
			<span>Back</span>
		</div>
	)
	return (
		<>
			{showListing ? (
				<ListingScreen
					isLoading={pageStatus !== 'Ready'}
					wipData={wipData}
					allStaff={allStaff}
					selectedJobLabel={selectedJobOption?.label}
					familyGroups={familyGroups}
					BackButton={backButton}
					filters={filters}
				/>
			) : (
				<IntroScreen
					isLoading={pageStatus !== 'Ready'}
					allStaff={allStaff}
					familyGroups={familyGroups}
					jobCategories={jobCategories}
					selectedJobOption={selectedJobOption}
					selectedClientOption={selectedClientOption}
					loadJobOptions={loadJobOptions}
					loadClientOptions={loadClientOptions}
					handleJobChange={handleJobChange}
					handleClientChange={handleClientChange}
					handleRunAnalysis={handleRunAnalysis}
					filters={filters}
				/>
			)}
		</>
	)
}

export default WIP
